import { useCallback, useEffect, useMemo, useState } from 'react';
import { Layout } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import { getApiErrorMessage } from '@api/server';
import { useAuth } from '@context/auth-context';
import { getAgents } from '../agents';
import { getChannels } from '../channels';
import {
  DialogMessages,
  clearMessageContext,
  getDialogMessages,
  getDialogs,
  updateMessageOperator,
} from '../dialogs';
import Dialogs from '../dialogs';

const Home = () => {
  const navigate = useNavigate();
  const { dialogId } = useParams();
  const { isAdmin } = useAuth();
  const [dialogs, setDialogs] = useState([]);
  const [channels, setChannels] = useState([]);
  const [agents, setAgents] = useState([]);
  const [messages, setMessages] = useState([]);
  const [dialogsError, setDialogsError] = useState('');
  const [messagesError, setMessagesError] = useState('');
  const [isDialogsLoading, setIsDialogsLoading] = useState(false);
  const [isMessagesLoading, setIsMessagesLoading] = useState(false);
  const [clearingMessageId, setClearingMessageId] = useState(null);
  const [updatingOperatorMessageId, setUpdatingOperatorMessageId] = useState(null);

  const selectedDialogId = useMemo(() => {
    const nextDialogId = Number(dialogId);
    return Number.isInteger(nextDialogId) && nextDialogId > 0 ? nextDialogId : null;
  }, [dialogId]);

  const selectedDialog = useMemo(
    () => dialogs.find((dialog) => dialog.id === selectedDialogId),
    [dialogs, selectedDialogId],
  );
  const channelNames = useMemo(() => (
    channels.reduce((names, channel) => {
      if (!names[channel.vid] || channel.active) {
        names[channel.vid] = channel.name;
      }
      return names;
    }, {})
  ), [channels]);
  const agentOptions = useMemo(() => agents.map((agent) => ({
    value: agent.code,
    label: `${agent.name} (${agent.code})`,
  })), [agents]);

  const handleSelectDialog = useCallback((nextDialogId) => {
    navigate(`/dialogs/${nextDialogId}`);
  }, [navigate]);

  const loadDialogs = useCallback(async () => {
    setDialogsError('');
    setIsDialogsLoading(true);
    try {
      const result = await getDialogs();
      setDialogs(result || []);
    } catch (error) {
      setDialogsError(getApiErrorMessage(error));
    } finally {
      setIsDialogsLoading(false);
    }
  }, []);

  const loadChannels = useCallback(async () => {
    if (!isAdmin) {
      setChannels([]);
      return;
    }

    try {
      const result = await getChannels();
      setChannels(result || []);
    } catch {
      setChannels([]);
    }
  }, [isAdmin]);

  const loadAgents = useCallback(async () => {
    if (!isAdmin) {
      setAgents([]);
      return;
    }

    try {
      const result = await getAgents();
      setAgents(result || []);
    } catch {
      setAgents([]);
    }
  }, [isAdmin]);

  const loadMessages = useCallback(async (dialogId, { silent = false } = {}) => {
    if (!dialogId) {
      setMessages([]);
      return;
    }

    setMessagesError('');
    if (!silent) {
      setIsMessagesLoading(true);
    }

    try {
      const result = await getDialogMessages(dialogId);
      setMessages([...(result || [])].reverse());
    } catch (error) {
      setMessagesError(getApiErrorMessage(error));
    } finally {
      if (!silent) {
        setIsMessagesLoading(false);
      }
    }
  }, []);

  const handleClearMessageContext = useCallback(async (message) => {
    setMessagesError('');
    setClearingMessageId(message.id);
    try {
      await clearMessageContext(message.id);
      setMessages((currentMessages) => currentMessages.map((currentMessage) => (
        currentMessage.id === message.id
          ? { ...currentMessage, response_id: null }
          : currentMessage
      )));
    } catch (error) {
      setMessagesError(getApiErrorMessage(error));
    } finally {
      setClearingMessageId(null);
    }
  }, []);

  const handleDialogContextCleared = useCallback((clearedDialogId) => {
    setDialogs((currentDialogs) => currentDialogs.map((dialog) => (
      dialog.id === clearedDialogId
        ? { ...dialog, context: null }
        : dialog
    )));
  }, []);

  const handleUpdateMessageOperator = useCallback(async (message, operator) => {
    if (!operator || operator === message.operator) {
      return;
    }

    setMessagesError('');
    setUpdatingOperatorMessageId(message.id);
    try {
      const result = await updateMessageOperator(message.id, operator);
      const nextOperator = result?.operator || operator;
      setMessages((currentMessages) => currentMessages.map((currentMessage) => (
        currentMessage.id === message.id
          ? { ...currentMessage, operator: nextOperator }
          : currentMessage
      )));
    } catch (error) {
      setMessagesError(getApiErrorMessage(error));
    } finally {
      setUpdatingOperatorMessageId(null);
    }
  }, []);

  useEffect(() => {
    loadDialogs();
  }, [loadDialogs]);

  useEffect(() => {
    loadChannels();
  }, [loadChannels]);

  useEffect(() => {
    loadAgents();
  }, [loadAgents]);

  useEffect(() => {
    loadMessages(selectedDialogId);
  }, [loadMessages, selectedDialogId]);

  useEffect(() => {
    if (!selectedDialogId) {
      return undefined;
    }

    const timerId = window.setInterval(() => {
      loadMessages(selectedDialogId, { silent: true });
    }, 10000);

    return () => window.clearInterval(timerId);
  }, [loadMessages, selectedDialogId]);

  return (
    <Layout className="dialogs-layout">
      <Dialogs
        dialogs={dialogs}
        dialogsError={dialogsError}
        isDialogsLoading={isDialogsLoading}
        selectedDialogId={selectedDialogId}
        onSelectDialog={handleSelectDialog}
        onReloadDialogs={loadDialogs}
        channelNames={channelNames}
      />

      <DialogMessages
        selectedDialog={selectedDialog}
        messages={messages}
        messagesError={messagesError}
        isMessagesLoading={isMessagesLoading}
        agentOptions={agentOptions}
        clearingMessageId={clearingMessageId}
        updatingOperatorMessageId={updatingOperatorMessageId}
        onReloadMessages={loadMessages}
        onClearMessageContext={handleClearMessageContext}
        onUpdateMessageOperator={handleUpdateMessageOperator}
        onDialogContextCleared={handleDialogContextCleared}
      />
    </Layout>
  );
};

export default Home;
